import Phaser from "phaser";
import { TOWERS } from "../config/towers";
import { loadSave } from "../systems/save";
import { totalStars } from "../systems/progression";
import { defenseAnalytics } from "../systems/analytics";
import {
  addButton,
  addCastleBackdrop,
  addLittleCastleFooter,
  addOrnatePanel,
  addStatusBadge,
  title,
} from "./shared";

const ACHIEVEMENT_NAMES: Readonly<Record<string, string>> = {
  "first-victory": "First Victory",
  "flawless-defense": "Flawless Defense",
  "hard-won-crown": "Hard-Won Crown",
  "fifty-waves": "Fifty Waves",
};

export class ProgressionScene extends Phaser.Scene {
  constructor() {
    super("Progression");
  }

  create(): void {
    addCastleBackdrop(this);
    const save = loadSave();
    const stars = totalStars(save.progress);
    const stats = save.progress.statistics ?? {
      victories: 0,
      defeats: 0,
      bossesDefeated: 0,
      highestWave: 0,
      totalScore: 0,
    };
    const unlocked = new Set(save.progress.unlockedTowers);
    const tier = Phaser.Math.Clamp(Math.floor((stars + stats.victories) / 8), 0, 4);
    const width = this.scale.width;
    const height = this.scale.height;
    const centerX = width / 2;
    const compact = width < 820 || height < 720;

    title(
      this,
      "The Growing Kingdom",
      "Victories raise new walls, unlock royal companies, and strengthen the realm.",
    );
    addButton(this, compact ? 82 : 95, 42, compact ? 126 : 150, "‹ Back", () =>
      this.scene.start("MainMenu"),
    );

    const castleTop = compact ? 205 : 220;
    const panelHeight = compact ? 150 : 176;
    const panelY = height - (compact ? 118 : 130);
    const availableCastleHeight = Math.max(210, panelY - panelHeight / 2 - castleTop - 26);
    const castleScale = Phaser.Math.Clamp(availableCastleHeight / 430, 0.52, 0.92);
    const castle = this.add
      .container(centerX, castleTop + availableCastleHeight / 2)
      .setScale(castleScale);
    const widths = [260, 330, 400, 470, 540];
    const keepWidth = widths[tier] ?? 260;

    const wall = this.add.graphics();
    wall.fillStyle(0x817d76).fillRoundedRect(-keepWidth / 2, -55, keepWidth, 170, 6);
    wall.lineStyle(5, 0x423f3b).strokeRoundedRect(-keepWidth / 2, -55, keepWidth, 170, 6);
    wall.fillStyle(0xa39e94).fillRoundedRect(-90 - tier * 10, -185, 180 + tier * 20, 245, 6);
    wall.lineStyle(5, 0x423f3b).strokeRoundedRect(-90 - tier * 10, -185, 180 + tier * 20, 245, 6);
    castle.add(wall);

    const keepRoof = this.add.triangle(
      0,
      -244,
      -112 - tier * 10,
      34,
      112 + tier * 10,
      34,
      0,
      -92,
      0x345d94,
    );
    castle.add(keepRoof);
    for (const x of [-keepWidth / 2 + 42, keepWidth / 2 - 42]) {
      castle.add(this.add.rectangle(x, -24, 76, 206, 0x918d84).setStrokeStyle(5, 0x423f3b));
      castle.add(this.add.triangle(x, -164, -52, 18, 52, 18, 0, -62, 0x345d94));
    }

    for (let index = 0; index <= tier; index += 1) {
      const denominator = Math.max(1, tier);
      const x = -keepWidth / 2 + 46 + (index * (keepWidth - 92)) / denominator;
      const pole = this.add.rectangle(x, -176, 4, 58, 0x6e5331);
      const flag = this.add.polygon(x + 17, -198, [0, 0, 34, 8, 0, 18], 0x2f5fa7);
      castle.add([pole, flag]);
      this.tweens.add({
        targets: flag,
        scaleX: 0.78,
        duration: 520 + index * 50,
        yoyo: true,
        repeat: -1,
      });
    }

    const panelGap = compact ? 12 : 24;
    const panelWidth = Math.min(compact ? width - 30 : 460, (width - panelGap * 3) / 2);
    const stacked = width < 720;
    const actualPanelWidth = stacked ? Math.min(width - 28, 560) : panelWidth;
    const leftX = stacked ? centerX : centerX - actualPanelWidth / 2 - panelGap / 2;
    const rightX = stacked ? centerX : centerX + actualPanelWidth / 2 + panelGap / 2;
    const firstPanelY = stacked ? height - 214 : panelY;
    const secondPanelY = stacked ? height - 76 : panelY;
    const actualPanelHeight = stacked ? 126 : panelHeight;

    const statsPanel = addOrnatePanel(this, leftX, firstPanelY, {
      width: actualPanelWidth,
      height: actualPanelHeight,
      accent: true,
    });
    const unlockPanel = addOrnatePanel(this, rightX, secondPanelY, {
      width: actualPanelWidth,
      height: actualPanelHeight,
      accent: true,
    });
    statsPanel.setDepth(10);
    unlockPanel.setDepth(10);

    this.add
      .text(leftX, firstPanelY - actualPanelHeight / 2 + 30, "ROYAL RECORD", {
        fontFamily: "Georgia, serif",
        fontSize: compact ? "17px" : "20px",
        fontStyle: "bold",
        color: "#f3c656",
      })
      .setOrigin(0.5)
      .setDepth(11);
    this.add
      .text(
        leftX,
        firstPanelY + 12,
        `Kingdom Tier ${tier + 1}/5\n${stars} campaign stars  •  ${save.progress.royalSeals ?? 0} royal seals\n${stats.victories} victories  •  ${stats.bossesDefeated} bosses defeated\nHighest wave ${stats.highestWave}  •  Score ${stats.totalScore.toLocaleString()}`,
        {
          fontFamily: "Trebuchet MS, sans-serif",
          fontSize: compact ? "12px" : "14px",
          color: "#fff3cf",
          align: "center",
          lineSpacing: compact ? 3 : 6,
        },
      )
      .setOrigin(0.5)
      .setDepth(11);

    this.add
      .text(rightX, secondPanelY - actualPanelHeight / 2 + 30, "ROYAL COLLECTION", {
        fontFamily: "Georgia, serif",
        fontSize: compact ? "17px" : "20px",
        fontStyle: "bold",
        color: "#f3c656",
      })
      .setOrigin(0.5)
      .setDepth(11);
    addStatusBadge(
      this,
      rightX,
      secondPanelY - (stacked ? 12 : 20),
      `${unlocked.size}/${Object.keys(TOWERS).length} COMPANIES`,
      "gold",
    ).setDepth(11);
    const achievements = save.progress.achievements ?? [];
    this.add
      .text(
        rightX,
        secondPanelY + (stacked ? 30 : 38),
        achievements.length > 0
          ? achievements.map((id) => `★ ${ACHIEVEMENT_NAMES[id] ?? id}`).join("   ")
          : "No achievements earned yet",
        {
          fontFamily: "Trebuchet MS, sans-serif",
          fontSize: compact ? "11px" : "13px",
          color: "#dfc889",
          align: "center",
          wordWrap: { width: actualPanelWidth - 42 },
        },
      )
      .setOrigin(0.5)
      .setDepth(11);

    addLittleCastleFooter(this, "Kingdom Chronicle");
    defenseAnalytics.track("progression_viewed", {
      tier: tier + 1,
      stars,
      victories: stats.victories,
      unlockedTowers: unlocked.size,
    });
  }
}
